import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Upload, X, Lock, Download, RefreshCw, Euro, Ban, Pencil, GitCompare } from "lucide-react";
import { useSortable, SortableTh } from "@/lib/sort";
import { toast } from "sonner";
import BulkPvlUpdateModal from "@/components/catalog/BulkPvlUpdateModal";
import PurgeCnModal from "@/components/catalog/PurgeCnModal";
import AddCnModal from "@/components/catalog/AddCnModal";

export default function CatalogPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [onlyNoGh, setOnlyNoGh] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showBulkPvl, setShowBulkPvl] = useState(false);
  const [showPurge, setShowPurge] = useState(false);
  const [showAddCn, setShowAddCn] = useState(false);
  const [form, setForm] = useState({ cn: "", name: "", is_efg: true, type: "generico", laboratorio: "", gh: "", pvl: "" });
  const [recalcBusy, setRecalcBusy] = useState(false);

  const [ghBusy, setGhBusy] = useState(false);

  // Inhabilita para el estudio las marcas cuyo G.H. no tiene genérico de ningún
  // laboratorio: pasan a "solo marca", que la app ya excluye de % genérico,
  // Top 25, semáforo e incentivos. Reversible desde el mismo botón.
  const disableGhWithoutGeneric = async () => {
    setGhBusy(true);
    try {
      const { data: prev } = await api.get("/admin/catalog/gh-without-generic");
      if (prev.already_disabled > 0 && prev.cn_count === 0) {
        if (!window.confirm(`Ya hay ${prev.already_disabled.toLocaleString("es-ES")} CN inhabilitados por no tener genérico.\n\n¿Quieres VOLVER A HABILITARLOS?`)) return;
        const { data } = await api.post("/admin/catalog/gh-without-generic/restore");
        toast.success(`${data.restored.toLocaleString("es-ES")} CN vueltos a habilitar`, { duration: 8000 });
        load();
        return;
      }
      if (prev.cn_count === 0) {
        toast.info("No hay ningún G.H. de marca sin genérico. Nada que inhabilitar.");
        return;
      }
      const ok = window.confirm(
        `Se van a inhabilitar ${prev.cn_count.toLocaleString("es-ES")} CN de marca de ${prev.gh_count.toLocaleString("es-ES")} G.H. que no tienen genérico de ningún laboratorio` +
        (prev.cn_without_gh_count ? ` (incluidos ${prev.cn_without_gh_count} sin G.H. asignado)` : "") +
        `.\n\nDejarán de contar en % de genérico, Top 25, semáforo e incentivos de todas las farmacias. No se borra nada: se puede deshacer.\n\n¿Continuamos?`
      );
      if (!ok) return;
      const t = toast.loading("Inhabilitando y recalculando estudios…");
      const { data } = await api.post("/admin/catalog/gh-without-generic/disable");
      await api.post("/dispensations/cleanup-orphan-ghs", null, { params: { all_users: true }, timeout: 300000 });
      toast.success(`${data.modified.toLocaleString("es-ES")} CN inhabilitados de ${data.gh_count.toLocaleString("es-ES")} G.H. sin genérico`, { id: t, duration: 10000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al inhabilitar los G.H. sin genérico");
    } finally {
      setGhBusy(false);
    }
  };

  const recalcAll = async () => {
    if (!window.confirm("¿Recalcular todos los estudios cargados con la información actual del vademécum? Re-enriquece nombres, lab, tipo y elimina líneas sin alternativa genérica en TODAS las farmacias.")) return;
    setRecalcBusy(true);
    const t = toast.loading("Recalculando estudios de todas las farmacias…");
    try {
      const { data } = await api.post("/dispensations/cleanup-orphan-ghs", null, { params: { all_users: true } });
      const removed = (data.total_orphan_no_gh || 0) + (data.total_only_marca_gh || 0);
      const enriched = data.total_re_enriched || 0;
      const parts = [];
      if (enriched > 0) parts.push(`${enriched.toLocaleString("es-ES")} línea(s) recalculada(s)`);
      if (removed > 0) parts.push(`${removed.toLocaleString("es-ES")} huérfana(s) eliminada(s)`);
      if (parts.length === 0) {
        toast.info("Ningún estudio necesitaba recalcularse.", { id: t });
      } else {
        toast.success(`Recalculo: ${parts.join(" · ")} en ${data.studies_affected} estudio(s).`, { id: t, duration: 10000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en el recálculo", { id: t });
    } finally {
      setRecalcBusy(false);
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/catalog").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = items.filter((i) => {
    if (onlyNoGh && (i.gh || "").trim()) return false;
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (
      i.cn.toLowerCase().includes(q) ||
      (i.name || "").toLowerCase().includes(q) ||
      (i.gh || "").toLowerCase().includes(q) ||
      (i.laboratorio || "").toLowerCase().includes(q)
    );
  });

  const noGhCount = items.filter((i) => !(i.gh || "").trim()).length;
  const { sorted, sortKey, direction, toggle } = useSortable(filtered, "name", "asc", "catalog-table");

  // Listas de opciones para los datalists del formulario "Añadir CN"
  // (GH y laboratorios existentes en el catálogo actual)
  const ghOptions = useMemo(() => {
    const s = new Set();
    for (const it of items) {
      const g = (it.gh || "").trim();
      if (g) s.add(g);
    }
    return Array.from(s).sort();
  }, [items]);

  const labOptions = useMemo(() => {
    const s = new Set();
    for (const it of items) {
      const l = (it.laboratorio || "").trim();
      if (l) s.add(l);
    }
    return Array.from(s).sort();
  }, [items]);

  // Índice GH → PVL más frecuente (para auto-rellenar PVL al elegir un GH existente)
  const pvlByGh = useMemo(() => {
    const counter = new Map(); // gh → Map(pvl → count)
    for (const it of items) {
      const g = (it.gh || "").trim();
      const p = it.pvl;
      if (!g || p == null) continue;
      const inner = counter.get(g) || new Map();
      inner.set(p, (inner.get(p) || 0) + 1);
      counter.set(g, inner);
    }
    const out = {};
    for (const [g, inner] of counter.entries()) {
      let best = null; let bestCount = 0;
      for (const [pv, c] of inner.entries()) {
        if (c > bestCount) { bestCount = c; best = pv; }
      }
      if (best != null) out[g] = best;
    }
    return out;
  }, [items]);

  // Cuando el admin cambia el GH, si existe y hay PVL conocido, lo auto-rellena.
  // Sólo sobrescribe si el usuario aún no ha metido un PVL propio o si el PVL
  // actual coincide con el sugerido por el GH anterior.
  const setGh = (newGh) => {
    setForm((prev) => {
      const suggested = pvlByGh[newGh.trim()];
      let nextPvl = prev.pvl;
      if (suggested != null) {
        const prevSuggested = pvlByGh[(prev.gh || "").trim()];
        if (prev.pvl === "" || prev.pvl == null || String(prev.pvl) === String(prevSuggested)) {
          nextPvl = suggested;
        }
      }
      return { ...prev, gh: newGh, pvl: nextPvl };
    });
  };

  // Aviso de CN ya existente: se muestra su G.H. y se puede corregir sin borrar
  const [dup, setDup] = useState(null);
  const [editing, setEditing] = useState(false);
  const checkCn = async (cn) => {
    const digits = String(cn || "").replace(/\D/g, "");
    if (digits.length < 6) return setDup(null);
    try {
      const { data } = await api.get(`/catalog/item/${digits}`);
      setDup(data.exists ? data.item : null);
    } catch {
      setDup(null);
    }
  };
  const loadDupIntoForm = () => {
    if (!dup) return;
    setEditing(true);
    setForm({
      cn: dup.cn || "",
      name: dup.name || "",
      is_efg: !!dup.is_efg,
      type: dup.type || "generico",
      laboratorio: dup.laboratorio || "",
      gh: dup.gh || "",
      pvl: dup.pvl == null ? "" : String(dup.pvl),
    });
    toast.info("Datos cargados: corrige lo que haga falta y pulsa Guardar");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cn || !form.name) {
      toast.error("CN y nombre son obligatorios");
      return;
    }
    const payload = {
      ...form,
      cn: form.cn.trim(),
      name: form.name.trim(),
      laboratorio: form.laboratorio.trim(),
      gh: form.gh.trim() || null,
      pvl: form.pvl === "" || form.pvl === null ? null : Number(form.pvl),
    };
    await api.post("/catalog", payload);
    toast.success(dup ? "Ficha actualizada" : "Guardado");
    setDup(null);
    setEditing(false);
    setShowForm(false);
    setForm({ cn: "", name: "", is_efg: true, type: "generico", laboratorio: "", gh: "", pvl: "" });
    load();
  };

  // Corregir una ficha existente desde la propia lista (sin borrarla)
  const editItem = (it) => {
    setForm({
      cn: it.cn || "",
      name: it.name || "",
      is_efg: !!it.is_efg,
      type: it.type || "generico",
      laboratorio: it.laboratorio || "",
      gh: it.gh || "",
      pvl: it.pvl == null ? "" : String(it.pvl),
    });
    setDup(it);
    setEditing(true);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (cn) => {
    if (!window.confirm(`¿Eliminar CN ${cn}?`)) return;
    await api.delete(`/catalog/${cn}`);
    toast.success("Eliminado");
    load();
  };

  const toggleField = async (cn, field, currentValue) => {
    if (!isAdmin) return;
    let newValue;
    if (field === "type") {
      newValue = currentValue === "marca" ? "generico" : "marca";
    } else if (field === "is_efg") {
      newValue = !currentValue;
    } else {
      return;
    }
    // Actualización optimista en UI
    setItems((prev) => prev.map((it) => (it.cn === cn ? { ...it, [field]: newValue } : it)));
    try {
      await api.post("/catalog/bulk", { cns: [cn], [field]: newValue });
      toast.success(field === "type" ? `Reclasificado como ${newValue}` : `EFG ${newValue ? "activado" : "desactivado"}`, { duration: 2000 });
    } catch (e) {
      // Revertir en caso de error
      setItems((prev) => prev.map((it) => (it.cn === cn ? { ...it, [field]: currentValue } : it)));
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/catalog/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.inserted} importados, ${data.skipped} ignorados`);
      // Resumen del auto-cleanup global tras subir catálogo
      const c = data.cleanup;
      if (c && (c.studies_affected > 0 || c.total_re_enriched > 0)) {
        const removed = (c.total_orphan_no_gh || 0) + (c.total_only_marca_gh || 0);
        const enriched = c.total_re_enriched || 0;
        const parts = [];
        if (enriched > 0) parts.push(`${enriched} línea(s) recalculada(s) con info del vademécum`);
        if (removed > 0) parts.push(`${removed} línea(s) huérfana(s) eliminadas`);
        toast.info(
          `Auto-recalculo: ${parts.join(" · ")} en ${c.studies_affected} estudio(s).`,
          { duration: 10000 }
        );
      } else if (c) {
        toast.info("Catálogo aplicado. Ningún estudio necesitaba recalcularse.", { duration: 4000 });
      }
      load();
    } catch (err) {
      toast.error("Error en la importación");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1400px]" data-testid="catalog-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">
            Maestro
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tighter text-slate-950">
            Catálogo central de códigos nacionales
          </h1>
          <p className="text-slate-500 mt-2 max-w-2xl">
            {isAdmin
              ? "Catálogo único compartido entre todas las farmacias del sistema. Cualquier cambio aquí se aplica automáticamente a todas las cuentas."
              : "Catálogo central gestionado por el administrador. Lo usas para clasificar tus dispensaciones; sólo el administrador puede modificarlo."}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin ? (
            <>
              <button
                data-testid="recalc-all-button"
                onClick={recalcAll}
                disabled={recalcBusy}
                title="Re-aplica el vademécum actual a todos los estudios cargados de todas las farmacias (re-enriquece y limpia huérfanos)"
                className="inline-flex items-center gap-2 bg-white border border-violet-300 text-violet-800 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-violet-50 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${recalcBusy ? "animate-spin" : ""}`} strokeWidth={1.5} />
                Recalcular todo ahora
              </button>
              <button
                data-testid="disable-gh-without-generic-button"
                onClick={disableGhWithoutGeneric}
                disabled={ghBusy}
                title="Inhabilita para el estudio las marcas cuyo G.H. no tiene genérico de ningún laboratorio (pasan a 'solo marca'). Reversible."
                className="inline-flex items-center gap-2 bg-white border border-amber-300 text-amber-800 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-amber-50 disabled:opacity-50"
              >
                <Ban className={`h-4 w-4 ${ghBusy ? "animate-pulse" : ""}`} strokeWidth={1.5} />
                Inhabilitar G.H. sin genérico
              </button>
              <button
                data-testid="export-catalog-button"
                onClick={async () => {
                  const t = toast.loading("Generando Excel del catálogo…");
                  try {
                    const r = await api.get("/admin/catalog/export", { responseType: "blob" });
                    const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
                    a.download = `catalogo_farmaclass_${ts}.xlsx`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success("Excel descargado", { id: t });
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Error al descargar", { id: t });
                  }
                }}
                title="Descarga todo el catálogo en Excel (útil para llevarlo a otro entorno y subirlo allí)"
                className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-900 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-50"
              >
                <Download className="h-4 w-4" strokeWidth={1.5} />
                Exportar Excel
              </button>
              <label
                data-testid="import-csv-button"
                className="cursor-pointer inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-900 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-50"
              >
                <Upload className="h-4 w-4" strokeWidth={1.5} />
                Importar Excel/CSV
                <input type="file" accept=".csv,.txt,.xlsx,.xls" className="hidden" onChange={handleFile} />
              </label>
              <label
                data-testid="compare-vademecum-button"
                title="Sube el vademécum de un laboratorio y descarga los CN que no están en el catálogo"
                className="cursor-pointer inline-flex items-center gap-2 bg-white border border-sky-300 text-sky-800 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-sky-50"
              >
                <GitCompare className="h-4 w-4" strokeWidth={1.75} />
                Comparar vademécum
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  data-testid="compare-vademecum-input"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (!f) return;
                    const t = toast.loading("Comparando con el catálogo…");
                    try {
                      const fd = new FormData();
                      fd.append("file", f);
                      const r = await api.post("/admin/catalog/compare", fd, { responseType: "blob" });
                      const total = r.headers["x-total-rows"];
                      const inCat = r.headers["x-in-catalog"];
                      const miss = r.headers["x-missing"];
                      const url = URL.createObjectURL(new Blob([r.data]));
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `faltan_${f.name.replace(/\.[^.]+$/, "")}.xlsx`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success(`${total} referencias · ${inCat} ya en el catálogo · faltan ${miss}`, { id: t, duration: 8000 });
                    } catch (err) {
                      toast.error("No se pudo comparar el fichero", { id: t });
                    }
                  }}
                />
              </label>
              <button
                data-testid="missing-gh-button"
                title="Descarga los CN del catálogo que no tienen G.H. asignado"
                onClick={async () => {
                  const t = toast.loading("Buscando CN sin G.H.…");
                  try {
                    const r = await api.get("/admin/catalog/missing-gh", { responseType: "blob" });
                    const n = r.headers["x-missing"];
                    const url = URL.createObjectURL(new Blob([r.data]));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "catalogo_sin_gh.xlsx";
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success(`${n} CN sin G.H.`, { id: t, duration: 6000 });
                  } catch (err) {
                    toast.error("No se pudo generar el Excel", { id: t });
                  }
                }}
                className="inline-flex items-center gap-2 bg-white border border-amber-300 text-amber-800 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-amber-50"
              >
                <Ban className="h-4 w-4" strokeWidth={1.75} />
                CN sin G.H.
              </button>
              <button
                data-testid="open-bulk-pvl-button"
                onClick={() => setShowBulkPvl(true)}
                title="Actualizar el PVL de todos los CN de un G.H. a la vez"
                className="inline-flex items-center gap-2 bg-white border border-emerald-300 text-emerald-800 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-emerald-50"
              >
                <Euro className="h-4 w-4" strokeWidth={1.75} />
                Actualizar PVL masivo
              </button>
              <button
                data-testid="open-add-cn-button"
                onClick={() => setShowAddCn(true)}
                title="Dar de alta CN genéricos desde un Excel (PVL tomado del G.H.)"
                className="inline-flex items-center gap-2 bg-white border border-emerald-300 text-emerald-800 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-emerald-50"
              >
                <Plus className="h-4 w-4" strokeWidth={1.75} />
                Añadir CN (Excel)
              </button>
              <button
                data-testid="open-purge-cn-button"
                onClick={() => setShowPurge(true)}
                title="Eliminar del catálogo, desde un Excel, los CN que ya no existen"
                className="inline-flex items-center gap-2 bg-white border border-rose-300 text-rose-800 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-rose-50"
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                Eliminar CN (Excel)
              </button>
              <button
                data-testid="add-catalog-button"
                onClick={() => {
                  if (showForm) { setShowForm(false); return; }
                  setEditing(false);
                  setDup(null);
                  setForm({ cn: "", name: "", is_efg: true, type: "generico", laboratorio: "", gh: "", pvl: "" });
                  setShowForm(true);
                }}
                className="inline-flex items-center gap-2 bg-slate-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800"
              >
                <Plus className="h-4 w-4" />
                Añadir CN
              </button>
            </>
          ) : (
            <div className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 text-slate-500 px-4 py-2.5 rounded-md text-xs font-medium">
              <Lock className="h-3.5 w-3.5" strokeWidth={1.5} />
              Catálogo gestionado por el administrador
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <form
          onSubmit={submit}
          data-testid="catalog-form"
          className="bg-white border border-slate-200 rounded-lg p-6 mb-6 grid grid-cols-1 md:grid-cols-6 gap-3"
        >
          {dup && (
            <div
              className="md:col-span-6 -mb-1 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900 flex flex-wrap items-center gap-2"
              data-testid="catalog-dup-warning"
            >
              <span className="font-bold">{editing ? "Corrigiendo esta ficha:" : "Este CN ya existe:"}</span>
              <span className="truncate">{dup.name}</span>
              <span className="text-amber-700">· G.H.: <b>{dup.gh || "sin G.H."}</b></span>
              {dup.laboratorio && <span className="text-amber-700">· {dup.laboratorio}</span>}
              {dup.pvl != null && <span className="text-amber-700">· PVL {dup.pvl} €</span>}
              {!editing && (
                <button
                  type="button"
                  onClick={loadDupIntoForm}
                  className="ml-auto px-2.5 py-1 rounded border border-amber-400 font-bold uppercase text-[10px] hover:bg-amber-100"
                  data-testid="catalog-dup-edit"
                >
                  Corregir esta ficha
                </button>
              )}
            </div>
          )}
          <input
            type="text"
            placeholder="CN"
            value={form.cn}
            onChange={(e) => { setForm({ ...form, cn: e.target.value }); setDup(null); }}
            onBlur={(e) => checkCn(e.target.value)}
            readOnly={editing}
            className={`px-3 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 ${editing ? "bg-slate-100 text-slate-500" : ""}`}
            required
            data-testid="catalog-cn-input"
          />
          <input
            type="text"
            placeholder="Nombre del producto"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="md:col-span-2 px-3 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
            required
            data-testid="catalog-name-input"
          />
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="px-3 py-2.5 border border-slate-200 rounded-md bg-white"
            data-testid="catalog-type-select"
          >
            <option value="generico">Genérico</option>
            <option value="marca">Marca</option>
          </select>
          <input
            type="text"
            placeholder="Laboratorio (elige uno o escribe)"
            list="catalog-lab-list"
            value={form.laboratorio}
            onChange={(e) => setForm({ ...form, laboratorio: e.target.value })}
            className="px-3 py-2.5 border border-slate-200 rounded-md"
            data-testid="catalog-lab-input"
          />
          <datalist id="catalog-lab-list">
            {labOptions.map((l) => <option key={l} value={l} />)}
          </datalist>
          <div className="flex gap-2">
            <button type="submit" data-testid="catalog-save-button" className="flex-1 bg-slate-950 text-white px-3 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800">
              {editing ? "Guardar cambios" : "Guardar"}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(false); setDup(null); }} className="px-3 py-2.5 border border-slate-200 rounded-md hover:bg-slate-50">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Segunda fila: G.H. (molécula) + PVL */}
          <div className="md:col-span-3">
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
              G.H. / Molécula
            </label>
            <input
              type="text"
              placeholder="Empieza a escribir para buscar (o escribe uno nuevo)"
              list="catalog-gh-list"
              value={form.gh}
              onChange={(e) => setGh(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-md"
              data-testid="catalog-gh-input"
            />
            <datalist id="catalog-gh-list">
              {ghOptions.map((g) => <option key={g} value={g} />)}
            </datalist>
            {form.gh && ghOptions.includes(form.gh.trim()) ? (
              <p className="mt-1 text-[10px] text-emerald-700">✓ G.H. existente — se agrupará con los demás CNs de esta molécula.</p>
            ) : form.gh.trim() ? (
              <p className="mt-1 text-[10px] text-amber-700">Nuevo G.H. — se creará como agrupación nueva.</p>
            ) : (
              <p className="mt-1 text-[10px] text-slate-400 italic">Opcional pero recomendado para agrupar equivalentes.</p>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
              PVL (€)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder={pvlByGh[form.gh.trim()] != null ? `${pvlByGh[form.gh.trim()]} (sugerido)` : "0,00"}
              value={form.pvl}
              onChange={(e) => setForm({ ...form, pvl: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-md font-mono-num"
              data-testid="catalog-pvl-input"
            />
            {form.gh.trim() && pvlByGh[form.gh.trim()] != null && (
              <p className="mt-1 text-[10px] text-slate-500">
                Auto-rellenado desde G.H. existente. Puedes ajustarlo si es diferente.
              </p>
            )}
          </div>
          <div className="md:col-span-1 flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_efg}
                onChange={(e) => setForm({ ...form, is_efg: e.target.checked })}
                className="h-4 w-4"
                data-testid="catalog-efg-checkbox"
              />
              Es EFG
            </label>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <input
            data-testid="catalog-search"
            type="text"
            placeholder="Buscar por CN, nombre, G.H. o laboratorio…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="flex-1 px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-amber-800 whitespace-nowrap cursor-pointer">
            <input
              type="checkbox"
              data-testid="catalog-only-no-gh"
              checked={onlyNoGh}
              onChange={(e) => setOnlyNoGh(e.target.checked)}
            />
            Solo CN sin G.H.
            <span className="font-mono-num text-amber-600">({noGhCount})</span>
          </label>
        </div>
        {loading ? (
          <div className="p-12 text-center text-slate-400">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            {items.length === 0
              ? "Aún no hay productos. Importa un CSV o añade uno manualmente."
              : "Sin resultados."}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="catalog-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-[0.15em] text-slate-500">
              <tr>
                <SortableTh k="cn" sortKey={sortKey} direction={direction} toggle={toggle} className="p-4">CN</SortableTh>
                <SortableTh k="name" sortKey={sortKey} direction={direction} toggle={toggle} className="p-4">Nombre</SortableTh>
                <SortableTh k="gh" sortKey={sortKey} direction={direction} toggle={toggle} className="p-4">G.H. / Molécula</SortableTh>
                <SortableTh k="type" sortKey={sortKey} direction={direction} toggle={toggle} className="p-4">Tipo</SortableTh>
                <SortableTh k="is_efg" sortKey={sortKey} direction={direction} toggle={toggle} className="p-4">EFG</SortableTh>
                <SortableTh k="pvl" sortKey={sortKey} direction={direction} toggle={toggle} className="p-4" align="right">PVL</SortableTh>
                <SortableTh k="laboratorio" sortKey={sortKey} direction={direction} toggle={toggle} className="p-4">Laboratorio</SortableTh>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {sorted.slice(0, 500).map((it) => (
                <tr key={it.cn} className="hover:bg-slate-50">
                  <td className="p-4 font-mono-num">{it.cn}</td>
                  <td className="p-4 font-medium">{it.name}</td>
                  <td className="p-4 text-slate-600" data-testid={`catalog-gh-${it.cn}`}>
                    {it.gh ? (
                      <span className="text-xs font-semibold text-slate-700">{it.gh}</span>
                    ) : (
                      <span className="text-xs text-amber-700 font-semibold">Sin G.H.</span>
                    )}
                  </td>
                  <td className="p-4">
                    {isAdmin ? (
                      <button
                        type="button"
                        onClick={() => toggleField(it.cn, "type", it.type)}
                        data-testid={`toggle-type-${it.cn}`}
                        title={`Click para cambiar a ${it.type === "marca" ? "genérico" : "marca"}`}
                        className={`px-2 py-1 rounded text-xs font-semibold cursor-pointer transition-all hover:ring-2 hover:ring-offset-1 ${it.type === "marca" ? "bg-slate-900 text-white hover:ring-slate-400" : "bg-blue-50 text-blue-700 hover:ring-blue-300"}`}
                      >
                        {it.type}
                      </button>
                    ) : (
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${it.type === "marca" ? "bg-slate-900 text-white" : "bg-blue-50 text-blue-700"}`}>
                        {it.type}
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    {isAdmin ? (
                      <button
                        type="button"
                        onClick={() => toggleField(it.cn, "is_efg", it.is_efg)}
                        data-testid={`toggle-efg-${it.cn}`}
                        title={`Click para ${it.is_efg ? "desactivar" : "activar"} EFG`}
                        className={`text-xs font-semibold px-2 py-1 rounded cursor-pointer transition-all hover:bg-slate-100 ${it.is_efg ? "text-emerald-700" : "text-slate-400"}`}
                      >
                        {it.is_efg ? "Sí" : "No"}
                      </button>
                    ) : (
                      it.is_efg ? (
                        <span className="text-emerald-700 text-xs font-semibold">Sí</span>
                      ) : (
                        <span className="text-slate-400 text-xs">No</span>
                      )
                    )}
                  </td>
                  <td className="p-4 text-right font-mono-num text-slate-600">
                    {it.pvl != null ? `${Number(it.pvl).toFixed(2)} €` : "—"}
                  </td>
                  <td className="p-4 text-slate-600">{it.laboratorio || "—"}</td>
                  <td className="p-4 text-right">
                    {isAdmin ? (
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`edit-cn-${it.cn}`}
                          onClick={() => editItem(it)}
                          title="Corregir esta ficha (nombre, G.H., laboratorio, PVL)"
                          className="text-slate-400 hover:text-slate-900 p-1.5"
                        >
                          <Pencil className="h-4 w-4" strokeWidth={1.6} />
                        </button>
                        <button
                          data-testid={`delete-cn-${it.cn}`}
                          onClick={() => remove(it.cn)}
                          className="text-slate-400 hover:text-red-600 p-1.5"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {filtered.length > 500 && (
          <div className="p-4 text-xs text-slate-400 border-t border-slate-200 text-center">
            Mostrando 500 de {filtered.length}. Refina la búsqueda.
          </div>
        )}
      </div>

      <div className="mt-8 text-xs text-slate-500 leading-relaxed bg-slate-50 border border-slate-200 rounded-lg p-5">
        <div className="font-semibold mb-1 text-slate-700">Formatos soportados de importación</div>
        <strong>Excel</strong> (xlsx/xls) con columnas: <code>Cnm, Nombre, Generico, Estudio, Pvl, G.H., Laboratorio</code> — donde <code>Generico</code> 1 = genérico / 0 = marca, y <code>Estudio</code> 1 = entra en estudio (EFG).
        <br />
        <strong>CSV</strong> (separador <code>,</code> o <code>;</code>): <code>cn,name,is_efg,type,laboratorio</code>.
      </div>

      {showBulkPvl && (
        <BulkPvlUpdateModal
          items={items}
          onClose={() => setShowBulkPvl(false)}
          onDone={load}
        />
      )}
      {showAddCn && (
        <AddCnModal onClose={() => setShowAddCn(false)} onDone={load} />
      )}
      {showPurge && (
        <PurgeCnModal onClose={() => setShowPurge(false)} onDone={load} />
      )}
    </div>
  );
}
